import { Entry } from 'contentful';
import NextImage, { ImageLoaderProps } from 'next/image';
import { useState } from 'react';
import { Button } from '../../../components/Clickables/Buttons/Button';
import { Container } from '../../../components/Container/Container';
import { Heading } from '../../../components/Heading/Heading';
import { CheckboxIcon } from '../../../components/Icons/CheckboxIcon';
import { Text } from '../../../components/Text/Text';
import { useIsTabletScreenWidthOrWider } from '@hooks/useScreenWidth';
import { ContentfulResizeTypes, getContentfulLoader } from '@lib/images/contentfulLoader';
import { TypeCmsProFeaturesListSkeleton } from '@generated/contentful';

export interface IProps {
  proFeaturesEntry: Entry<TypeCmsProFeaturesListSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>;
}

export const CMSProFeaturesSection = ({ proFeaturesEntry }: IProps) => {
  const isTabletScreenWidthOrWider = useIsTabletScreenWidthOrWider();
  const [showMore, setShowMore] = useState(false);
  const { phonePreviewImage, title, features } = proFeaturesEntry.fields;
  const phonePreview = phonePreviewImage?.fields.file?.url;
  const phoneWidth = phonePreviewImage?.fields.file?.details.image?.width ?? 0;
  const phoneHeight = phonePreviewImage?.fields.file?.details.image?.height;

  const contentfulLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getContentfulLoader(src, width, phoneWidth, phoneHeight, {
      quality,
      resize: ContentfulResizeTypes.FILL,
    });

  return (
    <Container
      className={`py-sm ${phonePreview ? 'flex flex-col items-center sm:flex-row' : ''}`}
      data-testid="CMSProFeaturesSection"
    >
      {phonePreview && (
        <div className="mb-10 flex w-full items-center justify-center md:w-1/3">
          <NextImage
            className="max-w-[300px] object-contain"
            alt="Fishbrain features"
            src={phonePreview}
            loader={contentfulLoader}
            width={phoneWidth}
            height={phoneHeight}
          />
        </div>
      )}

      <div
        className={`items-left flex flex-col sm:ml-2 sm:items-center md:mx-auto md:w-2/3 ${
          phonePreview ? '' : 'sm:w-col-10'
        }`}
      >
        <Heading className="mb-4 max-w-[680px] sm:mb-6" as="h3" variant="md">
          {title}
        </Heading>

        {!isTabletScreenWidthOrWider && (
          <>
            <ul className="mb-0">
              {features.slice(0, 3).map(feature => {
                if (!feature) {
                  return <></>;
                }
                const { title: featureTitle, body } = feature.fields;
                const _id = feature.sys.id;
                return (
                  <li className="flex" key={_id}>
                    <div className="mr-3">
                      <CheckboxIcon isGreen />
                    </div>
                    <div>
                      <Heading variant="xs">{featureTitle}</Heading>
                      <Text className="mb-0">{body}</Text>
                    </div>
                  </li>
                );
              })}
            </ul>

            {features.length > 3 && showMore && (
              <ul className="mb-0">
                {features.slice(3).map(feature => {
                  if (!feature) {
                    return <></>;
                  }
                  const { title: featureTitle, body } = feature.fields;
                  const _id = feature.sys.id;
                  return (
                    <li className="flex" key={_id}>
                      <div className="mr-3">
                        <CheckboxIcon isGreen />
                      </div>
                      <div>
                        <Heading variant="xs">{featureTitle}</Heading>
                        <Text className="mb-0">{body}</Text>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {features.length > 3 && !showMore && (
              <Button
                variant="buttonTertiary"
                size="sm"
                onClick={() => {
                  setShowMore(true);
                }}
              >
                More PRO features
              </Button>
            )}
          </>
        )}

        {isTabletScreenWidthOrWider && (
          <ul className="bg-lake-fog mb-0">
            {features.map(feature => {
              if (!feature) {
                return <></>;
              }
              const { title: featureTitle, body } = feature.fields;
              const _id = feature.sys.id;
              return (
                <li className="flex" key={_id}>
                  <div className="mr-3">
                    <CheckboxIcon isGreen />
                  </div>
                  <div>
                    <Heading variant="xs">{featureTitle}</Heading>
                    <Text className="mb-0">{body}</Text>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Container>
  );
};
